import * as moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  UncontrolledDropdown,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
  RSelect,
} from "../../components/Component";
import Table from "../../components/react-table/Table";
import { DELETE, GET } from "../../helper/api";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { CUSTOMER_TYPE } from "../../utils/Utils";
import UpsertCustomer from "./UpsertCustomer";

const CUSTOMER_TYPE_OPTIONS = Object.keys(CUSTOMER_TYPE).map((c) => ({
  label: CUSTOMER_TYPE[c],
  value: c,
}));

const DELIVERY_PROVIDER_OPTIONS = [
  { label: "Nhà vận chuyển", value: "true" },
  { label: "Không phải NVC", value: "false" },
];

const CustomerManager = () => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const clearPagi = useRef();
  const [data, setData] = useState([]);
  const [view, setView] = useState({
    edit: false,
    add: false,
    details: false,
    isDelete: false,
  });

  const routePath = "/customer";
  const [loading, setLoading] = useState(false);
  const [onSearchText, setSearchText] = useState("");

  const [editId, setEditedId] = useState();
  const [deleteId, setDeleteId] = useState();
  const [timeNow, setTimeNow] = useState();
  const [smOption, setSmOption] = useState(false);

  const [filter, setFilter] = useState({
    type: searchParams.get("type") || "IN",
    isDeliveryProvider: searchParams.get("isDeliveryProvider") || undefined,
  });
  const title = filter.type === "OUT" ? "Khách hàng" : "Nhà cung cấp";
  const label = title.toLowerCase();
  const colTable = [
    {
      Header: "Mã",
      accessor: "id",
    },
    {
      Header: `Tên ${label}`,
      accessor: "name",
    },
    {
      Header: "Nhà vận chuyển",
      accessor: (item) => (item.isDeliveryProvider ? "Có" : "Không"),
    },
    {
      Header: "Biển số xe",
      accessor: (item) => (
        <div style={{ whiteSpace: "pre-line" }}>
          {(item.licensePlates || []).join("\n")}
        </div>
      ),
    },
    {
      Header: "Mã số thuế",
      accessor: (item) => item.metadata?.taxCode || "",
    },
    {
      Header: "Số tài khoản",
      accessor: (item) => (
        <div>
          {(item.metadata?.bankAccounts || [])
            .filter((b) => b?.bankNumber)
            .map((b, idx) => (
              <div key={idx}>
                {b.bankNumber}
                {b.bankCode ? ` - ${b.bankCode}` : ""}
              </div>
            ))}
        </div>
      ),
    },
    {
      Header: "Hành động",
      accessor: (item) => (
        <ul className="nk-tb-actions gx-1">
          <li>
            <UncontrolledDropdown>
              <DropdownToggle
                tag="a"
                className="btn btn-icon dropdown-toggle btn-trigger"
              >
                <Icon name="more-h"></Icon>
              </DropdownToggle>
              <DropdownMenu right>
                <ul className="link-list-opt no-bdr">
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#edit"
                      onClick={(ev) => {
                        ev.preventDefault();
                        setEditedId(item.id);
                        toggle("edit");
                      }}
                    >
                      <Icon name="edit"></Icon>
                      <span>Cập nhật</span>
                    </DropdownItem>
                  </li>
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#money"
                      onClick={(ev) => {
                        ev.preventDefault();
                        history.push(`/customer/report-by-customer/${item.id}`);
                      }}
                    >
                      <Icon name="money"></Icon>
                      <span>Dòng tiền, nguyên liệu</span>
                    </DropdownItem>
                  </li>

                  <li>
                    <DropdownItem
                      tag="a"
                      href="#dropdown"
                      onClick={(ev) => {
                        ev.preventDefault();
                        setDeleteId(item.id);
                        toggle("isDelete");
                      }}
                    >
                      <Icon name="trash" className="text-danger"></Icon>
                      <span>Xoá</span>
                    </DropdownItem>
                  </li>
                </ul>
              </DropdownMenu>
            </UncontrolledDropdown>
          </li>
        </ul>
      ),
    },
  ];

  useEffect(() => {
    setLoading(true);
    setData([]);

    const newParams = new URLSearchParams({
      type: filter.type,
      ...(filter.isDeliveryProvider
        ? { isDeliveryProvider: filter.isDeliveryProvider }
        : {}),
    });
    history.replace({
      pathname: location.pathname,
      search: newParams.toString(),
    });

    clearTimeout(clearPagi.current);
    clearPagi.current = setTimeout(() => {
      let params = {
        type: filter.type,
        scope: "full",
      };
      if (filter.isDeliveryProvider) {
        params.isDeliveryProvider = filter.isDeliveryProvider;
      }
      if (onSearchText !== "") {
        params.search = onSearchText;
      }

      GET(`${routePath}`, params, true)
        .then(({ data }) => {
          setData([...data]);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 400);
  }, [onSearchText, timeNow, filter, routePath]);

  // toggle function to view order details
  const toggle = (type) => {
    setView({
      ...view,
      [type]: !view[type],
    });
  };

  const resetData = () => {
    onFormCancel();
    setSearchText("");
    setTimeNow(moment().valueOf());
  };

  // onChange function for searching name
  const onFilterChange = (e) => {
    setSearchText(e.target.value);
  };

  // function to close the form modal
  const onFormCancel = () => {
    setEditedId("");
    setDeleteId("");
    setView({ add: false, edit: false, isDelete: false });
  };

  const deleteOrder = (id) => {
    if (!id) {
      return;
    }
    DELETE(`${routePath}/${id}`, {}, true)
      .then((res) => {
        toast.success(`Xóa ${label} thành công`);
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      });
  };

  return (
    <React.Fragment>
      <Head title={`Quản lý ${label}`}></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>{title}</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                <div
                  className="toggle-expand-content"
                  style={{ display: smOption ? "block" : "none" }}
                >
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end">
                    <li>
                      <div className="form-control-wrap">
                        <div className="form-icon form-icon-right">
                          <Icon name="search"></Icon>
                        </div>
                        <input
                          type="text"
                          placeholder={`Tìm kiếm ${label}`}
                          className="form-control  border-primary"
                          value={onSearchText}
                          onChange={(e) => onFilterChange(e)}
                        />
                      </div>
                    </li>
                    <li>
                      <div className="form-control-wrap" style={{ width: 150 }}>
                        <RSelect
                          classNamePrefix="form-control"
                          isClearable
                          placeholder="Lọc theo loại"
                          options={CUSTOMER_TYPE_OPTIONS}
                          value={CUSTOMER_TYPE_OPTIONS.find(
                            (c) => c.value === filter.type
                          )}
                          onChange={(val) =>
                            setFilter({
                              ...filter,
                              type: val?.value || undefined
                            })
                          }
                        />
                      </div>
                    </li>
                    <li>
                      <div className="form-control-wrap" style={{ width: 170 }}>
                        <RSelect
                          classNamePrefix="form-control"
                          isClearable
                          placeholder="Nhà vận chuyển?"
                          options={DELIVERY_PROVIDER_OPTIONS}
                          value={
                            DELIVERY_PROVIDER_OPTIONS.find(
                              (c) => c.value === filter.isDeliveryProvider
                            ) || null
                          }
                          onChange={(val) =>
                            setFilter({
                              ...filter,
                              isDeliveryProvider: val?.value || undefined,
                            })
                          }
                        />
                      </div>
                    </li>
                    <li>
                      <div>
                        <Button
                          className="btn-primary"
                          color="primary"
                          onClick={() => {
                            onFormCancel();

                            toggle("add");
                          }}
                        >
                          <Icon name="plus"></Icon>
                          <span>Thêm {label}</span>
                        </Button>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered card-preview">
            <div className="card-inner">
              <div
                id="DataTables_Table_1_wrapper"
                className="dataTables_wrapper dt-bootstrap4 no-footer"
              >
                <div className="row justify-between g-2">
                  <div className="col-8 col-sm-6 text-start"></div>
                  <div className="col-5 col-sm-6 text-end">
                    <div className="datatable-filter">
                      <div className="d-flex justify-content-end g-2">
                        <div
                          className="dataTables_length"
                          id="DataTables_Table_1_length"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="my-3">
                  <Table
                    loading={loading}
                    columns={colTable}
                    data={data}
                    notShowPagi={true}
                    headerRow={
                      <tr className="font-weight-bold">
                        <td colSpan={7}>Tổng cộng:</td>
                        <td></td>
                      </tr>
                    }
                  ></Table>
                </div>
              </div>
            </div>
          </div>
        </Block>
        {(view.add || view.edit) && <UpsertCustomer
          routePath={routePath}
          setLoading={setLoading}
          type={filter.type}
          onSuccess={resetData}
          onFormCancel={onFormCancel}
          editId={editId}
          isOpen={true}
        />}
        <Modal isOpen={view.isDelete} toggle={() => onFormCancel()}>
          <ModalBody className="modal-body-lg text-center">
            <div className="nk-modal">
              <Icon className="nk-modal-icon icon-circle icon-circle-xxl ni ni-cross bg-danger"></Icon>
              <h4 className="nk-modal-title">
                Bạn có chắc muốn xoá {label} này không ?{" "}
              </h4>
              <div className="nk-modal-action mt-5 d-flex justify-content-around">
                <Button
                  color="light"
                  size="lg"
                  className="btn-mw mr-5"
                  onClick={() => onFormCancel()}
                >
                  Huỷ bỏ
                </Button>
                <Button
                  color="primary"
                  size="lg"
                  className="btn-mw"
                  onClick={(ev) => {
                    ev.preventDefault();
                    deleteOrder(deleteId);
                  }}
                >
                  Xác nhận
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default CustomerManager;
